import React, { useState, useEffect, useCallback } from 'react';
import Layout from '../components/Layout';
import { useTheme } from '../contexts/ThemeContext';
import { api } from '../config/api';
import {
  PlusIcon,
  XMarkIcon,
  ChevronDownIcon,
  ChevronUpIcon,
  ClipboardDocumentIcon,
  CheckIcon,
} from '@heroicons/react/24/outline';

interface ReferralSignup {
  id: string;
  user_id: string;
  user_email: string;
  user_full_name: string | null;
  signed_up_at: string;
}

interface ReferralCode {
  id: string;
  code: string;
  label: string;
  is_active: boolean;
  expires_at: string | null;
  created_by: string;
  created_at: string;
  updated_at: string | null;
  signup_count: number;
  creator_email: string | null;
}

interface ReferralCodeDetail extends ReferralCode {
  signups: ReferralSignup[];
}

const AdminReferrals: React.FC = () => {
  const { isDarkMode } = useTheme();
  const [codes, setCodes] = useState<ReferralCode[]>([]);
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [expandedCodeId, setExpandedCodeId] = useState<string | null>(null);
  const [codeDetail, setCodeDetail] = useState<ReferralCodeDetail | null>(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [copiedCode, setCopiedCode] = useState<string | null>(null);

  // Create form state
  const [newCode, setNewCode] = useState('');
  const [newLabel, setNewLabel] = useState('');
  const [newExpiresAt, setNewExpiresAt] = useState('');
  const [createError, setCreateError] = useState('');
  const [creating, setCreating] = useState(false);

  const fetchCodes = useCallback(async () => {
    try {
      setLoading(true);
      const response = await api.get('/api/admin/referrals');
      setCodes(response.data);
    } catch (error) {
      console.error('Failed to fetch referral codes:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchCodes();
  }, [fetchCodes]);

  const handleCreate = async (e: React.FormEvent) => {
    e.preventDefault();
    setCreateError('');
    setCreating(true);
    try {
      await api.post('/api/admin/referrals', {
        code: newCode,
        label: newLabel,
        expires_at: newExpiresAt || undefined,
      });
      setShowCreateModal(false);
      setNewCode('');
      setNewLabel('');
      setNewExpiresAt('');
      await fetchCodes();
    } catch (err: any) {
      setCreateError(err.response?.data?.detail || 'Failed to create referral code');
    } finally {
      setCreating(false);
    }
  };

  const handleToggleActive = async (code: ReferralCode) => {
    try {
      await api.patch(`/api/admin/referrals/${code.id}`, {
        is_active: !code.is_active,
      });
      await fetchCodes();
    } catch (error) {
      console.error('Failed to toggle referral code:', error);
    }
  };

  const handleDelete = async (code: ReferralCode) => {
    if (!window.confirm(`Delete referral code "${code.code}"? This cannot be undone.`)) return;
    try {
      await api.delete(`/api/admin/referrals/${code.id}`);
      await fetchCodes();
    } catch (err: any) {
      alert(err.response?.data?.detail || 'Failed to delete referral code');
    }
  };

  const handleExpand = async (codeId: string) => {
    if (expandedCodeId === codeId) {
      setExpandedCodeId(null);
      setCodeDetail(null);
      return;
    }
    setExpandedCodeId(codeId);
    setDetailLoading(true);
    try {
      const response = await api.get(`/api/admin/referrals/${codeId}`);
      setCodeDetail(response.data);
    } catch (error) {
      console.error('Failed to fetch code detail:', error);
    } finally {
      setDetailLoading(false);
    }
  };

  const copyReferralLink = (code: string) => {
    const url = `${window.location.origin}/signup?ref=${code}`;
    navigator.clipboard.writeText(url);
    setCopiedCode(code);
    setTimeout(() => setCopiedCode(null), 2000);
  };

  const formatDate = (dateStr: string) => {
    return new Date(dateStr).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  const inputClass = `appearance-none block w-full px-3 py-2.5 rounded-lg transition-all ${
    isDarkMode
      ? 'bg-slate-800/50 border border-slate-700 text-white placeholder-slate-500 focus:border-cyan-500'
      : 'bg-white border border-gray-300 text-gray-900 placeholder-gray-400 focus:border-cyan-500'
  } focus:outline-none focus:ring-2 focus:ring-cyan-500/20`;

  return (
    <Layout>
      <div className="max-w-6xl mx-auto px-4 py-8">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className={`text-2xl font-bold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
              Referral Codes
            </h1>
            <p className={`mt-1 text-sm ${isDarkMode ? 'text-slate-400' : 'text-gray-500'}`}>
              Create and manage referral codes to track signups
            </p>
          </div>
          <button
            onClick={() => setShowCreateModal(true)}
            className="flex items-center gap-2 px-4 py-2.5 rounded-xl text-sm font-semibold text-white transition-all"
            style={{
              background: 'linear-gradient(135deg, #06B6D4 0%, #3B82F6 100%)',
              boxShadow: '0 0 20px rgba(6, 182, 212, 0.2)',
            }}
          >
            <PlusIcon className="h-4 w-4" />
            Create Code
          </button>
        </div>

        {/* Stats summary */}
        {!loading && codes.length > 0 && (
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-8">
            <div className={`p-4 rounded-xl ${isDarkMode ? 'bg-slate-800/50 border border-slate-700/50' : 'bg-white border border-gray-200'}`}>
              <p className={`text-sm ${isDarkMode ? 'text-slate-400' : 'text-gray-500'}`}>Total Codes</p>
              <p className={`text-2xl font-bold mt-1 ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>{codes.length}</p>
            </div>
            <div className={`p-4 rounded-xl ${isDarkMode ? 'bg-slate-800/50 border border-slate-700/50' : 'bg-white border border-gray-200'}`}>
              <p className={`text-sm ${isDarkMode ? 'text-slate-400' : 'text-gray-500'}`}>Active Codes</p>
              <p className={`text-2xl font-bold mt-1 ${isDarkMode ? 'text-emerald-400' : 'text-emerald-600'}`}>
                {codes.filter(c => c.is_active).length}
              </p>
            </div>
            <div className={`p-4 rounded-xl ${isDarkMode ? 'bg-slate-800/50 border border-slate-700/50' : 'bg-white border border-gray-200'}`}>
              <p className={`text-sm ${isDarkMode ? 'text-slate-400' : 'text-gray-500'}`}>Total Signups</p>
              <p className={`text-2xl font-bold mt-1 ${isDarkMode ? 'text-cyan-400' : 'text-cyan-600'}`}>
                {codes.reduce((sum, c) => sum + c.signup_count, 0)}
              </p>
            </div>
          </div>
        )}

        {/* Codes table */}
        {loading ? (
          <div className="flex justify-center py-12">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-cyan-500"></div>
          </div>
        ) : codes.length === 0 ? (
          <div className={`text-center py-16 rounded-2xl ${isDarkMode ? 'bg-slate-800/30 border border-slate-700/50' : 'bg-gray-50 border border-gray-200'}`}>
            <p className={`text-lg ${isDarkMode ? 'text-slate-400' : 'text-gray-500'}`}>
              No referral codes yet
            </p>
            <p className={`text-sm mt-1 ${isDarkMode ? 'text-slate-500' : 'text-gray-400'}`}>
              Create your first referral code to start tracking signups
            </p>
          </div>
        ) : (
          <>
            {/* Mobile: Card layout */}
            <div className="md:hidden space-y-3">
            {codes.map((code) => (
              <div key={code.id} className={`rounded-xl overflow-hidden ${isDarkMode ? 'bg-slate-800/30 border border-slate-700/50' : 'bg-white border border-gray-200'}`}>
                <div
                  className={`p-4 cursor-pointer ${expandedCodeId === code.id ? (isDarkMode ? 'bg-slate-700/20' : 'bg-blue-50/50') : ''}`}
                  onClick={() => handleExpand(code.id)}
                >
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center gap-2">
                      <span className={`font-mono font-semibold ${isDarkMode ? 'text-cyan-400' : 'text-cyan-700'}`}>
                        {code.code}
                      </span>
                      <button
                        onClick={(e) => { e.stopPropagation(); copyReferralLink(code.code); }}
                        className={`p-1 rounded transition-colors ${isDarkMode ? 'hover:bg-slate-600' : 'hover:bg-gray-200'}`}
                      >
                        {copiedCode === code.code ? (
                          <CheckIcon className="h-4 w-4 text-emerald-500" />
                        ) : (
                          <ClipboardDocumentIcon className={`h-4 w-4 ${isDarkMode ? 'text-slate-400' : 'text-gray-400'}`} />
                        )}
                      </button>
                    </div>
                    <div className="flex items-center gap-2">
                      <button
                        onClick={(e) => { e.stopPropagation(); handleToggleActive(code); }}
                        className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium transition-colors ${
                          code.is_active
                            ? isDarkMode ? 'bg-emerald-500/15 text-emerald-400' : 'bg-emerald-100 text-emerald-700'
                            : isDarkMode ? 'bg-slate-600/30 text-slate-400' : 'bg-gray-100 text-gray-500'
                        }`}
                      >
                        {code.is_active ? 'Active' : 'Inactive'}
                      </button>
                      {expandedCodeId === code.id
                        ? <ChevronUpIcon className={`h-4 w-4 ${isDarkMode ? 'text-slate-400' : 'text-gray-400'}`} />
                        : <ChevronDownIcon className={`h-4 w-4 ${isDarkMode ? 'text-slate-400' : 'text-gray-400'}`} />
                      }
                    </div>
                  </div>
                  <p className={`text-sm ${isDarkMode ? 'text-slate-300' : 'text-gray-700'}`}>{code.label}</p>
                  <div className="flex items-center gap-4 mt-2">
                    <span className={`text-xs ${isDarkMode ? 'text-slate-400' : 'text-gray-500'}`}>
                      <span className={`font-semibold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>{code.signup_count}</span> signups
                    </span>
                    <span className={`text-xs ${isDarkMode ? 'text-slate-500' : 'text-gray-400'}`}>
                      {formatDate(code.created_at)}
                    </span>
                    {code.expires_at && (
                      <span className={`text-xs ${isDarkMode ? 'text-slate-500' : 'text-gray-400'}`}>
                        Exp: {formatDate(code.expires_at)}
                      </span>
                    )}
                    <button
                      onClick={(e) => { e.stopPropagation(); handleDelete(code); }}
                      className={`ml-auto p-1 rounded-lg text-xs transition-colors ${isDarkMode ? 'text-red-400 hover:bg-red-500/15' : 'text-red-600 hover:bg-red-50'}`}
                    >
                      <XMarkIcon className="h-4 w-4" />
                    </button>
                  </div>
                </div>

                {/* Expanded detail */}
                {expandedCodeId === code.id && (
                  <div className={`px-4 pb-4 ${isDarkMode ? 'bg-slate-800/40' : 'bg-gray-50'}`}>
                    {detailLoading ? (
                      <div className="flex justify-center py-4">
                        <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-cyan-500"></div>
                      </div>
                    ) : codeDetail && codeDetail.signups.length > 0 ? (
                      <div className="space-y-2">
                        <h4 className={`text-xs font-semibold uppercase ${isDarkMode ? 'text-slate-400' : 'text-gray-500'}`}>
                          Signups
                        </h4>
                        {codeDetail.signups.map((s) => (
                          <div key={s.id} className={`p-2.5 rounded-lg ${isDarkMode ? 'bg-slate-700/30' : 'bg-white border border-gray-100'}`}>
                            <p className={`text-sm font-medium truncate ${isDarkMode ? 'text-slate-300' : 'text-gray-700'}`}>{s.user_email}</p>
                            <div className="flex items-center justify-between mt-0.5">
                              <span className={`text-xs ${isDarkMode ? 'text-slate-500' : 'text-gray-400'}`}>{s.user_full_name || '-'}</span>
                              <span className={`text-xs ${isDarkMode ? 'text-slate-500' : 'text-gray-400'}`}>{formatDate(s.signed_up_at)}</span>
                            </div>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <p className={`text-sm text-center py-3 ${isDarkMode ? 'text-slate-500' : 'text-gray-400'}`}>
                        No signups yet
                      </p>
                    )}
                  </div>
                )}
              </div>
            ))}
          </div>

          {/* Desktop: Table layout */}
          <div className={`hidden md:block rounded-2xl overflow-hidden ${isDarkMode ? 'bg-slate-800/30 border border-slate-700/50' : 'bg-white border border-gray-200'}`}>
            <table className="w-full">
              <thead>
                <tr className={isDarkMode ? 'bg-slate-800/50' : 'bg-gray-50'}>
                  <th className={`px-6 py-3 text-left text-xs font-medium uppercase tracking-wider ${isDarkMode ? 'text-slate-400' : 'text-gray-500'}`}>Code</th>
                  <th className={`px-6 py-3 text-left text-xs font-medium uppercase tracking-wider ${isDarkMode ? 'text-slate-400' : 'text-gray-500'}`}>Label</th>
                  <th className={`px-6 py-3 text-center text-xs font-medium uppercase tracking-wider ${isDarkMode ? 'text-slate-400' : 'text-gray-500'}`}>Status</th>
                  <th className={`px-6 py-3 text-center text-xs font-medium uppercase tracking-wider ${isDarkMode ? 'text-slate-400' : 'text-gray-500'}`}>Signups</th>
                  <th className={`px-6 py-3 text-left text-xs font-medium uppercase tracking-wider ${isDarkMode ? 'text-slate-400' : 'text-gray-500'}`}>Created</th>
                  <th className={`px-6 py-3 text-left text-xs font-medium uppercase tracking-wider ${isDarkMode ? 'text-slate-400' : 'text-gray-500'}`}>Expires</th>
                  <th className={`px-6 py-3 text-right text-xs font-medium uppercase tracking-wider ${isDarkMode ? 'text-slate-400' : 'text-gray-500'}`}>Actions</th>
                </tr>
              </thead>
              <tbody className={`divide-y ${isDarkMode ? 'divide-slate-700/50' : 'divide-gray-100'}`}>
                {codes.map((code) => (
                  <React.Fragment key={code.id}>
                    <tr
                      className={`cursor-pointer transition-colors ${
                        isDarkMode ? 'hover:bg-slate-700/30' : 'hover:bg-gray-50'
                      } ${expandedCodeId === code.id ? (isDarkMode ? 'bg-slate-700/20' : 'bg-blue-50/50') : ''}`}
                      onClick={() => handleExpand(code.id)}
                    >
                      <td className="px-6 py-4">
                        <div className="flex items-center gap-2">
                          <span className={`font-mono font-semibold ${isDarkMode ? 'text-cyan-400' : 'text-cyan-700'}`}>
                            {code.code}
                          </span>
                          <button
                            onClick={(e) => { e.stopPropagation(); copyReferralLink(code.code); }}
                            className={`p-1 rounded transition-colors ${isDarkMode ? 'hover:bg-slate-600' : 'hover:bg-gray-200'}`}
                            title="Copy referral link"
                          >
                            {copiedCode === code.code ? (
                              <CheckIcon className="h-4 w-4 text-emerald-500" />
                            ) : (
                              <ClipboardDocumentIcon className={`h-4 w-4 ${isDarkMode ? 'text-slate-400' : 'text-gray-400'}`} />
                            )}
                          </button>
                        </div>
                      </td>
                      <td className={`px-6 py-4 text-sm ${isDarkMode ? 'text-slate-300' : 'text-gray-700'}`}>
                        {code.label}
                      </td>
                      <td className="px-6 py-4 text-center">
                        <button
                          onClick={(e) => { e.stopPropagation(); handleToggleActive(code); }}
                          className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium transition-colors ${
                            code.is_active
                              ? isDarkMode
                                ? 'bg-emerald-500/15 text-emerald-400 hover:bg-emerald-500/25'
                                : 'bg-emerald-100 text-emerald-700 hover:bg-emerald-200'
                              : isDarkMode
                                ? 'bg-slate-600/30 text-slate-400 hover:bg-slate-600/50'
                                : 'bg-gray-100 text-gray-500 hover:bg-gray-200'
                          }`}
                        >
                          {code.is_active ? 'Active' : 'Inactive'}
                        </button>
                      </td>
                      <td className="px-6 py-4 text-center">
                        <span className={`text-sm font-semibold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                          {code.signup_count}
                        </span>
                      </td>
                      <td className={`px-6 py-4 text-sm ${isDarkMode ? 'text-slate-400' : 'text-gray-500'}`}>
                        {formatDate(code.created_at)}
                      </td>
                      <td className={`px-6 py-4 text-sm ${isDarkMode ? 'text-slate-400' : 'text-gray-500'}`}>
                        {code.expires_at ? formatDate(code.expires_at) : '-'}
                      </td>
                      <td className="px-6 py-4 text-right">
                        <div className="flex items-center justify-end gap-2">
                          <button
                            onClick={(e) => { e.stopPropagation(); handleDelete(code); }}
                            className={`p-1.5 rounded-lg text-xs transition-colors ${
                              isDarkMode
                                ? 'text-red-400 hover:bg-red-500/15'
                                : 'text-red-600 hover:bg-red-50'
                            }`}
                            title="Delete"
                          >
                            <XMarkIcon className="h-4 w-4" />
                          </button>
                          {expandedCodeId === code.id
                            ? <ChevronUpIcon className={`h-4 w-4 ${isDarkMode ? 'text-slate-400' : 'text-gray-400'}`} />
                            : <ChevronDownIcon className={`h-4 w-4 ${isDarkMode ? 'text-slate-400' : 'text-gray-400'}`} />
                          }
                        </div>
                      </td>
                    </tr>

                    {/* Expanded detail row */}
                    {expandedCodeId === code.id && (
                      <tr>
                        <td colSpan={7} className={`px-6 py-4 ${isDarkMode ? 'bg-slate-800/40' : 'bg-gray-50'}`}>
                          {detailLoading ? (
                            <div className="flex justify-center py-4">
                              <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-cyan-500"></div>
                            </div>
                          ) : codeDetail && codeDetail.signups.length > 0 ? (
                            <div>
                              <h4 className={`text-sm font-semibold mb-3 ${isDarkMode ? 'text-slate-300' : 'text-gray-700'}`}>
                                Users who signed up with this code
                              </h4>
                              <div className={`rounded-lg overflow-hidden ${isDarkMode ? 'border border-slate-700/50' : 'border border-gray-200'}`}>
                                <table className="w-full">
                                  <thead>
                                    <tr className={isDarkMode ? 'bg-slate-700/30' : 'bg-gray-100'}>
                                      <th className={`px-4 py-2 text-left text-xs font-medium uppercase ${isDarkMode ? 'text-slate-400' : 'text-gray-500'}`}>Email</th>
                                      <th className={`px-4 py-2 text-left text-xs font-medium uppercase ${isDarkMode ? 'text-slate-400' : 'text-gray-500'}`}>Name</th>
                                      <th className={`px-4 py-2 text-left text-xs font-medium uppercase ${isDarkMode ? 'text-slate-400' : 'text-gray-500'}`}>Signed Up</th>
                                    </tr>
                                  </thead>
                                  <tbody className={`divide-y ${isDarkMode ? 'divide-slate-700/30' : 'divide-gray-100'}`}>
                                    {codeDetail.signups.map((s) => (
                                      <tr key={s.id}>
                                        <td className={`px-4 py-2 text-sm ${isDarkMode ? 'text-slate-300' : 'text-gray-700'}`}>{s.user_email}</td>
                                        <td className={`px-4 py-2 text-sm ${isDarkMode ? 'text-slate-400' : 'text-gray-500'}`}>{s.user_full_name || '-'}</td>
                                        <td className={`px-4 py-2 text-sm ${isDarkMode ? 'text-slate-400' : 'text-gray-500'}`}>{formatDate(s.signed_up_at)}</td>
                                      </tr>
                                    ))}
                                  </tbody>
                                </table>
                              </div>
                            </div>
                          ) : (
                            <p className={`text-sm text-center py-4 ${isDarkMode ? 'text-slate-500' : 'text-gray-400'}`}>
                              No signups yet for this code
                            </p>
                          )}
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                ))}
              </tbody>
            </table>
          </div>
          </>
        )}

        {/* Create modal */}
        {showCreateModal && (
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
            <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={() => setShowCreateModal(false)} />
            <div className={`relative w-full max-w-md rounded-2xl p-6 shadow-2xl ${
              isDarkMode
                ? 'bg-slate-900 border border-slate-700/50'
                : 'bg-white border border-gray-200'
            }`}>
              <div className="flex items-center justify-between mb-6">
                <h3 className={`text-lg font-semibold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                  Create Referral Code
                </h3>
                <button
                  onClick={() => setShowCreateModal(false)}
                  className={`p-1 rounded-lg transition-colors ${isDarkMode ? 'hover:bg-slate-800 text-slate-400' : 'hover:bg-gray-100 text-gray-500'}`}
                >
                  <XMarkIcon className="h-5 w-5" />
                </button>
              </div>

              {createError && (
                <div className={`mb-4 px-4 py-3 rounded-lg text-sm ${
                  isDarkMode
                    ? 'bg-red-500/10 border border-red-500/20 text-red-400'
                    : 'bg-red-50 border border-red-200 text-red-600'
                }`}>
                  {createError}
                </div>
              )}

              <form onSubmit={handleCreate} className="space-y-4">
                <div>
                  <label className={`block text-sm font-medium mb-1 ${isDarkMode ? 'text-slate-300' : 'text-gray-700'}`}>
                    Code
                  </label>
                  <input
                    type="text"
                    required
                    value={newCode}
                    onChange={(e) => setNewCode(e.target.value.toUpperCase())}
                    placeholder="e.g. VIDYA2024"
                    className={inputClass}
                    style={{ textTransform: 'uppercase' }}
                  />
                  <p className={`mt-1 text-xs ${isDarkMode ? 'text-slate-500' : 'text-gray-400'}`}>
                    3-50 characters, letters, numbers, hyphens, underscores
                  </p>
                </div>

                <div>
                  <label className={`block text-sm font-medium mb-1 ${isDarkMode ? 'text-slate-300' : 'text-gray-700'}`}>
                    Label
                  </label>
                  <input
                    type="text"
                    required
                    value={newLabel}
                    onChange={(e) => setNewLabel(e.target.value)}
                    placeholder="e.g. Product Hunt Launch"
                    className={inputClass}
                  />
                </div>

                <div>
                  <label className={`block text-sm font-medium mb-1 ${isDarkMode ? 'text-slate-300' : 'text-gray-700'}`}>
                    Expires At (Optional)
                  </label>
                  <input
                    type="datetime-local"
                    value={newExpiresAt}
                    onChange={(e) => setNewExpiresAt(e.target.value)}
                    className={inputClass}
                  />
                </div>

                <button
                  type="submit"
                  disabled={creating}
                  className="w-full py-3 px-4 rounded-xl text-sm font-semibold text-white transition-all disabled:opacity-50"
                  style={{
                    background: 'linear-gradient(135deg, #06B6D4 0%, #3B82F6 100%)',
                    boxShadow: '0 0 20px rgba(6, 182, 212, 0.2)',
                  }}
                >
                  {creating ? 'Creating...' : 'Create Referral Code'}
                </button>
              </form>
            </div>
          </div>
        )}
      </div>
    </Layout>
  );
};

export default AdminReferrals;
